<template>
  <div class="animated">
    <b-card>
      <div slot="header"><i class="icon-note"></i> Sticky Messages</div>
      <b-button
        :variant="isPremium ? 'success' : 'secondary'"
        v-on:click="openCreateModal"
        :disabled="!isPremium"
      >
        <font-awesome-icon icon="plus" style="margin-right: 5px" />Create new
        sticky message
      </b-button>
      <br />
      <br />

      <p v-show="data.length >= 15 && isPremium" class="text-warning">
        Sticky message limit reached (15/15)
      </p>

      <hr />
      <div v-if="loading">
        <div class="sk-circle-fade float-left">
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
        </div>
        <h2 class="float-left ml-4">Loading sticky messages...</h2>
      </div>
      <div
        v-else
        class="table-wrapper"
        :class="{ 'premium-disabled': !isPremium }"
      >
        <v-client-table
          :columns="columns"
          :data="data"
          :options="options"
          :theme="theme"
          id="dataTable"
          style="width: 100%; margin: none"
        >
          <p
            slot="channel"
            class="d-none d-md-inline-block"
            slot-scope="props"
            style="max-width: 780px !important"
            block
          >
            #{{ channel_from_id(props.row.channel_id).name }}
          </p>
          <p
            slot="content"
            class="d-none d-md-inline-block"
            slot-scope="props"
            style="max-width: 780px !important"
            block
          >
            {{ props.row.content }}
            <b-badge
              v-if="Object.keys(JSON.parse(props.row.embed)).length > 1"
              class="ml-2"
              variant="primary"
              pill
              >embed</b-badge
            >
          </p>
          <p slot="expires" slot-scope="props" block>
            {{ props.row.expires_at ? toLocal(props.row.expires_at) : " " }}
          </p>
          <b-button
            slot="actions"
            slot-scope="props"
            block
            :variant="isPremium ? 'outline-primary' : 'outline-secondary'"
            v-on:click="openEditModal(props.row)"
            >Edit</b-button
          >
        </v-client-table>
      </div>
    </b-card>

    <!-- Edit Modal -->
    <b-modal
      size="lg"
      v-model="editModal"
      @ok="editModal = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-note" style="margin-right: 10px"></i>Edit Sticky
          Message
        </h4>
      </template>
      <b-form-group>
        <h4 class="smalltitle">Channel (Read-only)</h4>
        <b-form-input
          type="text"
          class="col-12"
          :value="'#' + channel_from_id(selected_channel_id).name"
          disabled
        ></b-form-input>
        <hr />
        <h4 class="smalltitle">Expires At (Read-only)</h4>
        <b-form-input
          type="text"
          class="col-12"
          :value="selected_expires_at ? toLocal(selected_expires_at) : ''"
          disabled
        ></b-form-input>
        <hr />
        <h4 class="smalltitle">Content</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            placeholder="Enter sticky message content"
            class="col-12"
            v-model="message_content"
          ></b-form-textarea>
        </b-input-group>
        <small>{{ message_content.length }}/2000</small>
        <hr />
        <EmbedBuilder ref="embed_builder_edit"></EmbedBuilder>
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2">
              <b-button
                v-b-tooltip.hover
                title="Delete sticky message"
                variant="ghost-danger"
                v-on:click="deleteStickyMessage"
              >
                <i class="icon-trash"></i>
              </b-button>
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="ghost-secondary" v-on:click="editModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="primary" v-on:click="editStickyMessage"
                >Save</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>

    <!-- Create Modal -->
    <b-modal
      size="lg"
      v-model="createModal"
      @ok="createModal = false"
      :no-enforce-focus="true"
    >
      <template slot="modal-title">
        <h4 class="smalltitle">
          <i class="icon-note" style="margin-right: 10px"></i>Create Sticky
          Message
        </h4>
      </template>
      <b-form-group>
        <h4 class="smalltitle" v-if="isPremium && templates.length > 0">
          Template (Optional)
        </h4>
        <b-form-select
          v-if="isPremium && templates.length > 0"
          v-model="selected_template"
          @change="applyTemplate"
        >
          <option :value="null">-- Select a template (optional) --</option>
          <option
            v-for="template in templates"
            :key="template.id"
            :value="template.id"
          >
            {{ template.name }}
          </option>
        </b-form-select>
        <hr v-if="isPremium && templates.length > 0" />

        <h4 class="smalltitle">Channel</h4>
        <multiselect
          v-model="selected_channel"
          id="channel_select"
          track-by="id"
          label="name"
          placeholder="Please select a channel"
          open-direction="bottom"
          :clear-on-select="false"
          :close-on-select="true"
          :max-height="600"
          :show-no-results="false"
          :hide-selected="false"
          :options="groupedChannels"
          :allow-empty="false"
          group-values="channels"
          group-label="category"
          :group-select="false"
        >
          <template slot="singleLabel" slot-scope="props">
            <span class="option__desc">
              <font-awesome-icon style="color: #72767d" icon="hashtag" />
              <span class="option__title ml-2">{{ props.option.name }}</span>
            </span>
          </template>
          <template slot="option" slot-scope="props">
            <div class="option__desc">
              <span class="option__title">
                <font-awesome-icon
                  style="color: #72767d"
                  :icon="!props.option['$isLabel'] ? 'hashtag' : 'chevron-down'"
                />
                {{ props.option.name || props.option.$groupLabel.name }}
              </span>
            </div>
          </template>
        </multiselect>
        <hr />

        <h4 class="smalltitle">Content</h4>
        <b-input-group>
          <b-form-textarea
            rows="6"
            type="text"
            placeholder="Enter sticky message content"
            class="col-12"
            v-model="message_content"
          ></b-form-textarea>
        </b-input-group>
        <small>{{ message_content.length }}/2000</small>
        <hr />

        <EmbedBuilder ref="embed_builder_create"></EmbedBuilder>
        <hr />

        <h4 class="smalltitle">Duration (Max duration 365 days )</h4>

        <b-row>
          <b-col cols="4">
            <b-form-group label="Days">
              <b-form-input
                type="number"
                v-model="duration_days"
                @input="handleDaysInput"
                placeholder="0"
                v-on:keydown="handleNonDecimalInput"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Hours">
              <b-form-input
                type="number"
                v-model="duration_hours"
                @input="handleHoursInput"
                placeholder="0"
                v-on:keydown="handleNonDecimalInput"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="Minutes">
              <b-form-input
                type="number"
                v-model="duration_minutes"
                @input="handleMinsInput"
                placeholder="0"
                v-on:keydown="handleNonDecimalInput"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <small
          >Sticky messages can be configured for a maximum duration of up to 365
          days</small
        >
      </b-form-group>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right: 0px"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2"></b-col>
            <b-col cols="auto" class="p-2">
              <b-button
                variant="ghost-secondary"
                v-on:click="createModal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="success" v-on:click="createStickyMessage"
                >Create</b-button
              >
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import { Switch as cSwitch } from "@coreui/vue";
import { ClientTable, Event } from "vue-tables-2";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import EmbedBuilder from "@/views/oc/EmbedBuilder";
import Multiselect from "vue-multiselect";
import PatronCard from "@/views/oc/PatronCard";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn",
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast,
};
Vue.use(VueNotifications, toast_options);
Vue.use(ClientTable);

export default {
  name: "StickyMessages",
  props: {
    isPremium: {
      type: Boolean,
    },
    patron: {
      type: Boolean,
    },
  },
  components: {
    ClientTable,
    Event,
    cSwitch,
    EmbedBuilder,
    Multiselect,
    PatronCard,
  },

  notifications: {
    added_message: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky message created",
    },
    edited_message: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky message edited",
    },
    deleted_message: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "Sticky message deleted",
    },
    fill_in_fields: {
      type: VueNotifications.types.warn,
      title: "Uh oh",
      message: "Please fill in all fields",
    },
    create_error: {
      type: VueNotifications.types.error,
      title: "Uh oh",
      message: "Something went wrong!",
    },
  },
  data: function () {
    return {
      columns: ["channel", "content", "expires", "actions"],
      data: [],
      templates: [],
      channels: [],
      selected_template: null,
      selected_channel: null,
      selected_channel_id: null,
      selected_expires_at: null,
      message_content: "",
      duration_days: 0,
      duration_hours: 0,
      duration_minutes: 0,
      options: {
        filterByColumn: true,
        columnsDisplay: {
          content: "not_mobile",
          expires: "not_mobile",
        },
        texts: {
          filter: "Filter",
          filterBy: "Filter by {column}",
        },
        sortable: ["channel", "expires"],
        filterable: ["channel", "content"],
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort",
        },
        pagination: {
          chunk: 5,
          edge: false,
          nav: "scroll",
        },
        childRow: false,
        highlightMatches: true,
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default",
      editModal: false,
      createModal: false,
      loading: true,
    };
  },
  methods: {
    handleHoursInput(value) {
      const num = parseInt(value);
      if (num > 24) {
        this.duration_hours = 0;
      } else if (num < 0) {
        this.duration_hours = 24;
      }
    },
    handleMinsInput(value) {
      const num = parseInt(value);
      if (num > 59) {
        this.duration_minutes = 0;
      } else if (num < 0) {
        this.duration_minutes = 59;
      }
    },
    handleDaysInput(value) {
      const num = parseInt(value);
      if (num < 0) {
        this.duration_days = 365;
      }
      if (num > 365) {
        this.duration_days = 0;
      }
    },
    toLocal(date_string) {
      var event = new Date(date_string + "Z");
      return event.toLocaleString();
    },
    channel_from_id(id) {
      return (
        this.channels.find((x) => x.id == id) || {
          name: "unknown channel",
        }
      );
    },
    applyTemplate() {
      if (!this.selected_template) return;

      // Fetch the template data
      this.axios
        .get(
          "/api/v1/servers/" + this.$route.params.guild_id + "/sticky-templates"
        )
        .then((r) => {
          const template = r.data.templates.find(
            (t) => t.id === this.selected_template
          );
          if (template) {
            this.message_content = template.content;
            const embed_data = JSON.parse(template.embed);

            this.$refs.embed_builder_create.apply_json(embed_data);
            this.$refs.embed_builder_create.show_embed = false;
          }
        });
    },
    openEditModal(row) {
      this.message_content = row.content.trim();
      this.selected_channel_id = row.channel_id;
      this.selected_expires_at = row.expires_at;
      this.editModal = true;
      var embed_var = JSON.parse(row.embed);
      setTimeout(() => {
        this.$refs.embed_builder_edit.apply_json(embed_var);
        this.$refs.embed_builder_edit.show_embed =
          Object.keys(embed_var).length > 1;
        this.$forceUpdate();
      }, 20);
    },
    openCreateModal() {
      this.message_content = "";
      this.selected_channel = null;
      this.selected_template = null;
      this.duration_days = 0;
      this.duration_hours = 0;
      this.duration_minutes = 0;
      this.createModal = true;
      setTimeout(() => {
        this.$refs.embed_builder_create.resetEmbed();
        this.$refs.embed_builder_create.show_embed = false;
      }, 20);
    },
    editStickyMessage() {
      if (
        !this.message_content.trim() &&
        Object.keys(this.$refs.embed_builder_edit.full_json).length < 2
      ) {
        return this.fill_in_fields();
      }

      this.axios
        .patch(
          "/api/v1/servers/" + this.$route.params.guild_id + "/sticky-messages",
          {
            channel_id: this.selected_channel_id,
            content: this.message_content,
            embed: this.$refs.embed_builder_edit.full_json,
          }
        )
        .then((r) => {
          if (!r.data.success) {
            this.create_error({ message: r.data.msg });
            return;
          }
          this.edited_message();
          this.closeModals();
          this.loadData();
        });
    },
    createStickyMessage() {
      if (
        !this.message_content.trim() &&
        Object.keys(this.$refs.embed_builder_create.full_json).length < 2
      ) {
        return this.fill_in_fields();
      }

      if (!this.selected_channel) {
        return this.fill_in_fields();
      }

      // Validate duration
      const total_hours =
        parseInt(this.duration_hours) +
        parseInt(this.duration_minutes) / 60 +
        parseInt(this.duration_days) * 24;
      if (total_hours > 24 * 365) {
        return this.create_error({ message: " Maximum duration is 365 days" });
      }

      if (
        parseInt(this.duration_hours) == 0 &&
        parseInt(this.duration_minutes) == 1
      ) {
        return this.create_error({
          message: "Minimum duration should be more than 1 minute",
        });
      }

      if (
        parseInt(this.duration_hours) == 0 &&
        parseInt(this.duration_minutes) == 0 &&
        parseInt(this.duration_days) == 0
      ) {
        return this.create_error({
          message: "Duration cannot be zero",
        });
      }

      this.axios
        .post(
          "/api/v1/servers/" + this.$route.params.guild_id + "/sticky-messages",
          {
            channel_id: this.selected_channel.id,
            content: this.message_content,
            embed: this.$refs.embed_builder_create.full_json,
            duration_days: parseInt(this.duration_days) || 0,
            duration_hours: parseInt(this.duration_hours) || 0,
            duration_minutes: parseInt(this.duration_minutes) || 0,
          }
        )
        .then((r) => {
          if (!r.data.success) {
            this.create_error({ message: r.data.msg });
            return;
          }
          this.added_message();
          this.closeModals();
          this.loadData();
        })
        .catch((err) => {
          this.create_error({
            message:
              (err.response && err.response.data && err.response.data.msg) ||
              "Failed to create sticky message",
          });
        });
    },
    deleteStickyMessage() {
      this.axios
        .delete(
          "/api/v1/servers/" +
            this.$route.params.guild_id +
            "/sticky-messages/" +
            this.selected_channel_id
        )
        .then(() => {
          this.deleted_message();
          this.closeModals();
          this.loadData();
        });
    },
    closeModals() {
      this.editModal = false;
      this.createModal = false;
    },
    loadData() {
      // Use props instead of making API call
      this.axios
        .get(
          "/api/v1/servers/" + this.$route.params.guild_id + "/sticky-messages"
        )
        .then((r) => {
          this.data = r.data.sticky_messages;
          this.templates = r.data.templates;
          this.channels = r.data.channels;
          this.loading = false;
          this.$Progress.finish();
        })
        .catch((error) => {
          console.error("Error loading messages:", error);
          this.loading = false;
          this.$Progress.finish();
        });
    },
  },
  computed: {
    groupedChannels() {
      let groups = [
        {
          category: {
            id: null,
            name: "Uncategorized",
          },
          channels: [],
        },
      ];
      let currentGroup = groups[0];
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          currentGroup = { category: chn, channels: [] };
          groups.push(currentGroup);
        }
      }
      for (let chn of this.channels.sort((a, b) => a.position - b.position)) {
        if (chn.type === 4) {
          continue;
        }
        currentGroup = groups.find((g) => {
          return g.category.id == chn.parent_id;
        });
        if (!currentGroup) {
          continue;
        }
        currentGroup.channels.push(chn);
      }
      return groups;
    },
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.loadData();
  },
  created: function () {
    this.$Progress.start();
    this.loadData();
  },
};
</script>

<style lang="scss">
#dataTable {
  width: 95%;
  margin: 0 auto;

  .VuePagination {
    text-align: center;
    justify-content: center;
  }

  .td {
    word-wrap: break-word !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vue-title {
    text-align: center;
    margin-bottom: 10px;
  }

  .VueTables__search-field {
    display: flex;
  }
  .VueTables__search-field input {
    margin-left: 0.25rem;
  }

  .VueTables__limit-field {
    display: flex;
  }

  .VueTables__limit-field select {
    margin-left: 0.25rem !important;
  }

  .VueTables__table th {
    text-align: center;
  }

  .VueTables__child-row-toggler {
    width: 16px;
    height: 16px;
    line-height: 16px;
    display: block;
    margin: auto;
    text-align: center;
  }

  .VueTables__child-row-toggler--closed::before {
    content: "+";
  }

  .VueTables__child-row-toggler--open::before {
    content: "-";
  }
}
.table-wrapper {
  position: relative;
}

.premium-disabled {
  .VueTables {
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  }
}

.premium-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.premium-message {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffc107;

  h4 {
    color: #ffc107;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    margin-bottom: 1.5rem;
  }
}

// Add to your existing styles
.table-wrapper {
  position: relative;
}

.premium-disabled {
  .VueTables {
    opacity: 0.55;
    pointer-events: none;
    user-select: none;
  }
}

.premium-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
}

.premium-message {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid #ffc107;

  h4 {
    color: #ffc107;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    margin-bottom: 1.5rem;
  }
}
</style>
